// KeyVisualUploader.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../../util/api";

// MUI
import {
    Alert,
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { notify } from "../../../../util/toast";
import { keyVisualApi } from "../api/keyVisualApi";
import AppAutocomplete from "../../../../component/common/AppAutocomplete";
import AssetPicker from "./AssetPicker";

import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import LoadingDots from "../../../../component/common/LoadingDots";
import ActionButton from "../../../../component/common/ActionButton";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { ds } from "../../../../styles/designSystem";
import { runApiAction } from "../../../../util/apiFeedback";

export default function KeyVisualUploader() {
    const API_BASE = process.env.REACT_APP_API_BASE_URL;

    const fileInputRef = useRef(null);
    const previewUrlRef = useRef(""); // ObjectURL revoke용

    const [form, setForm] = useState({ file: null, customName: "", projectName: "", preview: "" });
    const [result, setResult] = useState({ keywords: [], selectedKeywords: [], filePath: "" });
    const [registeredKeywords, setRegisteredKeywords] = useState([]);
    const [projectOptions, setProjectOptions] = useState([]);

    const [loading, setLoading] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [manualKeyword, setManualKeyword] = useState("");

    /**
     * 로고(BI·CI)·레퍼런스 이미지 — 확정 저장 <b>전에는 메모리에만</b> 들고 있는다.
     * 서버가 이 파일들을 붙일 대상(키비주얼 id)이 확정 저장 응답으로만 나오기 때문이다.
     */
    const [logoFiles, setLogoFiles] = useState([]);
    const [referenceFiles, setReferenceFiles] = useState([]);


    // ✅ 정책: trim + 모든 공백 제거 (중간 공백도 제거됨)
    //    "선 우" => "선우"
    const normalizeKeyword = useCallback((s) => {
        return String(s ?? "")
            .trim()
            .replace(/\s+/g, "");
    }, []);

    // ✅ 콤마로 여러 개 입력 지원
    // "선우, 홍 길동,테  스  트" => ["선우","홍길동","테스트"]
    const normalizeParts = useCallback(
        (text) => {
            return String(text ?? "")
                .trim()
                .replace(/,+$/, "") // 끝 콤마 제거
                .split(",")
                .map((p) => normalizeKeyword(p))
                .filter(Boolean);
        },
        [normalizeKeyword]
    );

    const fetchRegisteredKeywords = useCallback(async () => {
        try {
            const data = await api.get(`${API_BASE}/key-visual/2d/registered-keywords`);
            const list = Array.isArray(data) ? data : (data?.data || []);
            const unique = Array.from(
                new Set(list.map((x) => normalizeKeyword(x)).filter(Boolean))
            );
            setRegisteredKeywords(unique);
        } catch (e) {
            console.error(e);
            notify("등록된 키워드 목록을 불러오지 못했습니다.", "error");
        }
    }, [API_BASE, normalizeKeyword]);

    useEffect(() => {
        fetchRegisteredKeywords();
    }, [fetchRegisteredKeywords]);

    // 프로젝트명 자동완성 옵션 — 2D/3D 통합 API. 등록된 프로젝트로 묶기 쉽게 미리 로드.
    useEffect(() => {
        (async () => {
            try {
                const data = await keyVisualApi.listProjectNames();
                setProjectOptions(Array.isArray(data) ? data : (data?.data || []));
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    // ✅ preview object url revoke (메모리 누수 방지)
    useEffect(() => {
        return () => {
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        };
    }, []);

    const resetAll = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = "";
        }

        setForm({ file: null, customName: "", projectName: "", preview: "" });
        setResult({ keywords: [], selectedKeywords: [], filePath: "" });
        setManualKeyword("");
        setLogoFiles([]);
        setReferenceFiles([]);

        if (fileInputRef.current) fileInputRef.current.value = "";
    }, []);

    const handleFileChange = useCallback(
        async (e) => {
            const selected = e.target.files?.[0];
            if (!selected) return;

            // 키비주얼 이름·프로젝트명 사전검증은 제거 — 한글 IME 합성 중 즉시 파일 선택 시
            // state 갱신 타이밍 차이로 빈 값 오인이 날 수 있음. 임시 업로드(OCR) 는 backend
            // 입장에서도 이름 정보 없이 동작하므로 confirm 시점에만 검증한다.
            const posterName = form.customName.trim();

            // 업로드 전 registered 갱신(추천 정확도)
            await fetchRegisteredKeywords();

            // preview url
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
            const previewUrl = URL.createObjectURL(selected);
            previewUrlRef.current = previewUrl;

            setForm((prev) => ({ ...prev, file: selected, preview: previewUrl }));
            setResult({ keywords: [], selectedKeywords: [], filePath: "" });
            setManualKeyword("");

            const formData = new FormData();
            formData.append("file", selected);
            formData.append("name", posterName);

            await runApiAction({
                request: () => api.upload(`${API_BASE}/key-visual/2d/upload`, formData),
                errorMsg: "키워드 추출에 실패했습니다.",
                validate: (data) => data?.filePath ? true : "업로드된 파일 경로가 비어 있습니다.",
                setLoading,
                onSuccess: async (data) => {

                // 키워드 파싱
                let keywords = [];
                const raw = data?.keywords;

                if (Array.isArray(raw)) {
                    keywords = raw.flatMap((k) => (typeof k === "string" ? k.split(/\s+/) : [String(k)]));
                } else if (typeof raw === "string") {
                    keywords = raw.split(/\s+/);
                }

                // ✅ 공백제거 normalizeKeyword 기준으로 유니크/정렬
                const uniqueSorted = Array.from(
                    new Set(keywords.map((x) => normalizeKeyword(x)).filter(Boolean))
                ).sort((a, b) => a.localeCompare(b, "ko"));

                // 추출된 키워드는 자동 선택하지 않는다 — 사용자가 칩을 직접 클릭해 선택한다.
                setResult({
                    keywords: uniqueSorted,
                    filePath: data?.filePath || "",
                    selectedKeywords: [],
                });

                    notify("키워드 추출 완료", "success");
                },
                onError: (err) => console.error(err),
            });
        },
        [API_BASE, fetchRegisteredKeywords, form.customName, normalizeKeyword]
    );

    // ✅ 선택/해제: 항상 normalizeKeyword 기준으로 동작 (중복/공백차이 문제 방지)
    const toggleKeyword = useCallback(
        (k) => {
            const key = normalizeKeyword(k);
            if (!key) return;

            setResult((prev) => {
                const selected = (prev.selectedKeywords || []).map(normalizeKeyword).filter(Boolean);
                const set = new Set(selected);

                if (set.has(key)) set.delete(key);
                else set.add(key);

                return {
                    ...prev,
                    selectedKeywords: Array.from(set),
                };
            });
        },
        [normalizeKeyword]
    );

    // ✅ 수동 키워드: 콤마로 여러 개 입력 가능 + 공백 제거 + 중복 제거 + 자동 선택
    const addManualKeyword = useCallback(
        (text) => {
            // 길이 제한 없음 — "봄", "빛" 같은 한 글자 키워드가 실제로 쓰인다.
            // 빈 문자열은 normalizeParts 의 filter(Boolean) 이 이미 걸러낸다.
            const parts = normalizeParts(text);
            if (parts.length === 0) return;

            setResult((prev) => {
                const prevKeywords = (prev.keywords || []).map(normalizeKeyword).filter(Boolean);
                const prevSelected = (prev.selectedKeywords || []).map(normalizeKeyword).filter(Boolean);

                const kwSet = new Set(prevKeywords);
                const selSet = new Set(prevSelected);

                const nextKeywords = [...prevKeywords];
                const nextSelected = [...prevSelected];

                let addedCount = 0;

                for (const p of parts) {
                    if (!kwSet.has(p)) {
                        nextKeywords.push(p);
                        kwSet.add(p);
                        addedCount++;
                    }
                    if (!selSet.has(p)) {
                        nextSelected.push(p);
                        selSet.add(p);
                    }
                }

                if (addedCount === 0) {
                    notify("추가할 키워드가 없습니다. (모두 중복)", "warning");
                    return prev;
                }

                notify(`${addedCount}개 키워드가 추가되었습니다.`, "success");

                return {
                    ...prev,
                    keywords: nextKeywords,
                    selectedKeywords: nextSelected,
                };
            });
        },
        [normalizeKeyword, normalizeParts]
    );

    const handleManualKeywordKeyDown = useCallback(
        (e) => {
            /*
             * 한글 조합 중의 Enter 는 무시한다.
             *
             * IME 로 "한글" 을 치는 동안 입력값은 한 → 한ㄱ → 한그 → 한글 로 계속 바뀐다.
             * 이때 누른 Enter 는 <b>조합을 확정하는 키</b>이지 "입력을 마쳤다" 는 뜻이 아닌데,
             * 그대로 처리하면 아직 확정 전 값(한)이 먼저 등록되고 확정 후 Enter 로 한글이 또 등록돼
             * <b>"한", "한글" 두 개</b>가 생긴다. 실제로 이 증상으로 신고가 들어왔다.
             *
             * {@code isComposing} 은 조합 중일 때만 true 라, 영문·숫자 입력에는 영향이 없다.
             * 3D 쪽(SpaceContentUploader)은 처음부터 이 가드가 있었고 2D 에만 빠져 있었다.
             */
            if (e.nativeEvent?.isComposing) return;
            if (e.key !== "Enter") return;
            e.preventDefault();

            if (!manualKeyword.trim()) {
                setManualKeyword("");
                return;
            }

            addManualKeyword(manualKeyword);
            setManualKeyword("");
        },
        [addManualKeyword, manualKeyword]
    );

    const handleConfirm = useCallback(async () => {
        const posterName = form.customName.trim();
        if (!posterName) {
            notify("키비주얼 이름을 입력해주세요.", "warning");
            return;
        }

        const projectName = form.projectName.trim();
        if (!projectName) {
            notify("프로젝트명을 입력해주세요.", "warning");
            return;
        }

        if (!result.filePath) {
            notify("파일 경로가 없습니다.", "error");
            return;
        }

        // ✅ 저장 payload도 normalizeKeyword 기준으로 정리
        const selected = Array.from(
            new Set((result.selectedKeywords || []).map(normalizeKeyword).filter(Boolean))
        );

        if (selected.length < 1) {
            notify("키워드를 최소 1개 이상 선택해주세요.", "warning");
            return;
        }

        const payload = {
            name: posterName,
            projectName,
            filePath: result.filePath,
            keywords: selected,
        };

        await runApiAction({
            request: () => api.post(`${API_BASE}/key-visual/2d/confirm`, payload),
            errorMsg: "저장 실패",
            successMsg: "저장 완료",
            setLoading,
            onSuccess: async (data) => {
                /*
                 * 로고·레퍼런스는 키비주얼이 저장된 <b>뒤</b>에 올린다 — 붙일 대상 id 가
                 * 이 응답으로만 나오기 때문이다(서버 confirm 이 id 를 반환하도록 바뀌었다).
                 *
                 * 여기서 실패해도 키비주얼 등록 자체는 성공한 상태다. 그래서 전체를 실패로
                 * 되돌리지 않고 "본체는 저장됐고 첨부만 실패했다" 를 정확히 알린다 —
                 * 뭉뚱그리면 사용자가 처음부터 다시 올리고, 같은 이름이라 중복 오류를 만난다.
                 */
                const newId = data?.data ?? data;
                if (newId && (logoFiles.length > 0 || referenceFiles.length > 0)) {
                    try {
                        if (logoFiles.length > 0) {
                            await keyVisualApi.addAssets(newId, "LOGO", logoFiles);
                        }
                        if (referenceFiles.length > 0) {
                            await keyVisualApi.addAssets(newId, "REFERENCE", referenceFiles);
                        }
                    } catch (e) {
                        console.error(e);
                        notify(
                            "키비주얼은 저장됐지만 로고·레퍼런스 첨부에 실패했습니다. "
                            + "목록에서 해당 항목을 열어 '수정'에서 다시 첨부해 주세요.",
                            "error"
                        );
                    }
                }

                resetAll();
                await fetchRegisteredKeywords();
                // 새 프로젝트면 자동완성 옵션에도 반영
                try {
                    const data2 = await keyVisualApi.listProjectNames();
                    setProjectOptions(Array.isArray(data2) ? data2 : (data2?.data || []));
                } catch (e) {
                    console.error(e);
                }
            },
            onError: (e) => console.error(e),
        });
    }, [
        API_BASE,
        fetchRegisteredKeywords,
        form.customName,
        form.projectName,
        logoFiles,
        normalizeKeyword,
        referenceFiles,
        resetAll,
        result.filePath,
        result.selectedKeywords,
    ]);

    /**
     * ⭐ 추천 판정 — 등록된 키워드가 추출 키워드와 "유사한가".
     *
     * <p>한 글자 키워드는 <b>정확히 같을 때만</b> 유사로 본다. 부분일치로 두면 "봄" 하나가
     * "봄축제"·"봄바람"·"새봄" 에 전부 걸려 등록 키워드 대부분에 ⭐ 가 붙고, 추천이 아무 신호도 주지 못한다.
     * (한 글자 등록을 허용하면서 같이 생긴 문제다 — 예전엔 2글자 미만이 아예 못 들어왔다.)
     */
    const isSimilarKeyword = useCallback(
        (registered, extracted) => {
            const e = normalizeKeyword(extracted);
            if (!e) return false;
            return e.length <= 1 ? registered === e : registered.includes(e);
        },
        [normalizeKeyword]
    );

    // ✅ 표시용: registeredKeywords는 이미 normalizeKeyword된 배열이지만, 한번 더 안전하게 정리
    const sortedRegistered = useMemo(() => {
        const reg = Array.from(new Set((registeredKeywords || []).map(normalizeKeyword).filter(Boolean)));

        return reg.sort((a, b) => {
            const isA = (result.keywords || []).some((k) => isSimilarKeyword(a, k));
            const isB = (result.keywords || []).some((k) => isSimilarKeyword(b, k));
            if (isA && !isB) return -1;
            if (!isA && isB) return 1;
            return a.localeCompare(b, "ko");
        });
    }, [isSimilarKeyword, normalizeKeyword, registeredKeywords, result.keywords]);

    const handleFileSelectClick = useCallback(() => {
        if (loading) return;
        if (form.file) {
            setShowConfirmModal(true);
        } else {
            fileInputRef.current?.click();
        }
    }, [form.file, loading]);

    const confirmFileReplace = useCallback(() => {
        const currentName = form.customName;
        setShowConfirmModal(false);
        resetAll();
        setForm((prev) => ({ ...prev, customName: currentName }));
        fileInputRef.current?.click();
    }, [form.customName, resetAll]);

    const cancelFileReplace = useCallback(() => {
        setShowConfirmModal(false);
    }, []);

    const handleReset = useCallback(() => {
        resetAll();
        notify("초기화 완료", "success");
    }, [resetAll]);

    const hasResult = (result.keywords || []).length > 0;

    // ✅ Chip 렌더용 selectedSet (normalize 기준)
    const selectedSet = useMemo(() => {
        return new Set((result.selectedKeywords || []).map(normalizeKeyword).filter(Boolean));
    }, [normalizeKeyword, result.selectedKeywords]);

    return (
        <Box sx={{ maxWidth: 980, mx: "auto", p: 2.5 }}>
            <Stack spacing={2.25}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography sx={ds.typography.pageTitle}>키비주얼 업로더</Typography>
                        <Typography sx={{ ...ds.typography.pageSubtitle, mt: 0.5 }}>
                            이미지 업로드 → 키워드 추출 → 선택/추가 → 확정 저장
                        </Typography>
                    </Box>

                    <ActionButton
                        variant="secondary"
                        startIcon={<RestartAltRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={handleReset}
                        disabled={loading}
                        label="초기화"
                    />
                </Stack>

                {(result.selectedKeywords || []).length > 0 && (
                    <Paper variant="outlined" sx={{ ...ds.components.external.card, p: 2 }}>
                        <Typography sx={{ fontWeight: 700, color: "text.secondary", fontSize: 13, mb: 1 }}>
                            선택된 키워드
                        </Typography>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {Array.from(selectedSet).map((k) => (
                                <Chip
                                    key={k}
                                    label={k}
                                    sx={ds.components.external.chipActive}
                                />
                            ))}
                        </Stack>
                    </Paper>
                )}

                <Paper variant="outlined" sx={{ ...ds.components.external.card, p: 2.25 }}>
                    <Stack spacing={2}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "text.secondary", mb: 0.75 }}>
                                    <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                                    키비주얼 이름
                                </Typography>
                                <TextField
                                    value={form.customName}
                                    onChange={(e) => setForm((prev) => ({ ...prev, customName: e.target.value }))}
                                    placeholder="목록에 표시될 키비주얼 이름을 입력하세요"
                                    size="small"
                                    fullWidth
                                    disabled={loading}
                                    required
                                    helperText="파일명으로 자동 입력되지 않습니다."
                                    sx={ds.components.external.inputSoft}
                                />

                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "text.secondary", mt: 1.25, mb: 0.75 }}>
                                    <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                                    프로젝트명
                                </Typography>
                                <AppAutocomplete
                                    freeSolo
                                    options={projectOptions}
                                    value={form.projectName}
                                    inputValue={form.projectName}
                                    onChange={(v) =>
                                        setForm((prev) => ({ ...prev, projectName: v == null ? "" : String(v) }))
                                    }
                                    onInputChange={(v) =>
                                        setForm((prev) => ({ ...prev, projectName: v }))
                                    }
                                    disabled={loading}
                                    placeholder="기존 프로젝트 선택 또는 새 이름 입력"
                                    helperText="동일 프로젝트로 묶으면 목록·검색에서 함께 표시됩니다."
                                    variant="outlined" disableUnderline={false}
                                    textFieldSx={ds.components.external.inputSoft}
                                />

                                {/* 부가 파일 — 키비주얼 본체와 달리 키워드 추출도 워터마크도 타지 않는다.
                                    확정 저장 후에 올라가므로 여기서는 목록만 만들어 둔다. */}
                                <Divider sx={{ my: 1.5 }} />
                                <Stack spacing={1.5}>
                                    <AssetPicker
                                        label="로고 (BI·CI)"
                                        hint="여러 개 선택할 수 있습니다. 확정 저장할 때 함께 올라갑니다."
                                        files={logoFiles}
                                        onFilesAdded={(picked) => setLogoFiles((p) => [...p, ...picked])}
                                        onRemoveLocal={(i) => setLogoFiles((p) => p.filter((_, j) => j !== i))}
                                        disabled={loading}
                                    />
                                    <AssetPicker
                                        label="레퍼런스 이미지"
                                        hint="시안 참고용 이미지입니다. 키워드 추출 대상이 아닙니다."
                                        files={referenceFiles}
                                        onFilesAdded={(picked) => setReferenceFiles((p) => [...p, ...picked])}
                                        onRemoveLocal={(i) => setReferenceFiles((p) => p.filter((_, j) => j !== i))}
                                        disabled={loading}
                                    />
                                </Stack>

                                {hasResult && (
                                    <>
                                        <Divider sx={{ my: 1.5 }} />

                                        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                                            <Box component="span" sx={{ color: ds.colors.error, mr: 0.4 }}>*</Box>
                                            추출된 키워드 (클릭해서 선택/해제)
                                        </Typography>

                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            {(result.keywords || []).map((k, i) => {
                                                const nk = normalizeKeyword(k);
                                                const selected = selectedSet.has(nk);

                                                return (
                                                    <Chip
                                                        key={`${nk}-${i}`}
                                                        label={nk}
                                                        clickable
                                                        onClick={() => toggleKeyword(nk)}
                                                        sx={selected ? ds.components.external.chipActive : ds.components.external.chip}
                                                    />
                                                );
                                            })}
                                        </Stack>

                                        <TextField
                                            value={manualKeyword}
                                            onChange={(e) => setManualKeyword(e.target.value)}
                                            onKeyDown={handleManualKeywordKeyDown}
                                            placeholder="키워드 입력 후 Enter (여러 개는 , 로 구분 / 공백 자동 제거)"
                                            size="small"
                                            sx={{
                                                mt: 2,
                                                "& .MuiOutlinedInput-root": { bgcolor: "rgba(0,0,0,0.02)" },
                                            }}
                                            disabled={loading}
                                        />
                                    </>
                                )}
                            </Box>

                            <Box sx={{ width: { xs: "100%", md: 240 } }}>
                                {form.preview ? (
                                    <Box
                                        component="img"
                                        src={form.preview}
                                        alt="preview"
                                        sx={{
                                            width: "100%",
                                            height: 240,
                                            borderRadius: 3,
                                            objectFit: "contain",
                                            border: "1px solid rgba(0,0,0,0.10)",
                                            bgcolor: "rgba(0,0,0,0.02)",
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: 240,
                                            borderRadius: 3,
                                            border: "1px dashed rgba(0,0,0,0.20)",
                                            bgcolor: "rgba(0,0,0,0.01)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "text.secondary",
                                            fontWeight: 800,
                                        }}
                                    >
                                        미리보기 없음
                                    </Box>
                                )}
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} justifyContent="flex-start">
                            <ActionButton
                                variant="upload"
                                onClick={handleFileSelectClick}
                                loading={loading}
                                label={loading ? "처리 중" : form.file ? "다른 파일 선택" : "파일 선택하기"}
                            />

                            <input
                                id="file-input"
                                type="file"
                                accept=".png,.jpg,.jpeg"
                                onChange={handleFileChange}
                                ref={fileInputRef}
                                disabled={loading}
                                style={{ display: "none" }}
                            />
                        </Stack>
                    </Stack>
                </Paper>

                {sortedRegistered.length > 0 && (
                    <Paper variant="outlined" sx={{ ...ds.components.external.card, p: 2.25 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 15 }}>등록된 키워드</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "text.secondary", mt: 0.5 }}>
                            ⭐ 표시는 추출 키워드와 유사한 항목입니다.
                        </Typography>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                            {sortedRegistered.map((k, i) => {
                                const nk = normalizeKeyword(k);
                                const isSelected = selectedSet.has(nk);
                                const isRecommended = (result.keywords || []).some((gpt) => isSimilarKeyword(nk, gpt));

                                return (
                                    <Chip
                                        key={`${nk}-${i}`}
                                        clickable
                                        onClick={() => toggleKeyword(nk)}
                                        label={`${isRecommended ? "⭐ " : ""}${nk}`}
                                        sx={{
                                            borderRadius: 6,
                                            height: 28,
                                            px: 0.25,
                                            ...(isSelected
                                                ? ds.components.external.chipActive
                                                : isRecommended
                                                    ? {
                                                        bgcolor: "rgba(255, 193, 7, 0.14)",
                                                        border: "1px solid rgba(255, 193, 7, 0.35)",
                                                        "&:hover": { bgcolor: "rgba(255, 193, 7, 0.20)" },
                                                    }
                                                    : ds.components.external.chip),
                                            "& .MuiChip-label": {
                                                fontWeight: isSelected ? 800 : 700,
                                                fontSize: 13,
                                                letterSpacing: "-0.2px",
                                                px: 1,
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    </Paper>
                )}

                {hasResult && (
                    <Stack direction="row" justifyContent="flex-end">
                        <ActionButton
                            variant="confirm"
                            startIcon={<CheckCircleRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={handleConfirm}
                            loading={loading}
                            label={loading ? "저장 중" : "확정 저장"}
                            sx={{ px: 3 }}
                        />
                    </Stack>
                )}
            </Stack>

            <Backdrop
                open={loading}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 10, bgcolor: "rgba(255,255,255,0.85)" }}
            >
                <LoadingDots label="처리하고 있어요" />
            </Backdrop>

            <Dialog
                open={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 1.5,
                        border: `1px solid ${ds.colors.border}`,
                        boxShadow: "0 18px 60px rgba(0,0,0,0.18)",
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 800 }}>파일을 다시 선택하시겠습니까?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontWeight: 700, color: "text.secondary" }}>기존 키비주얼 정보와 키워드가 초기화됩니다.</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    <ActionButton variant="cancel" onClick={cancelFileReplace} />
                    <ActionButton variant="confirm" startIcon={false} onClick={confirmFileReplace} />
                </DialogActions>
            </Dialog>

        </Box>
    );
}
